// The real MediaRoom (MAJLIS-9, A-3, TS-3): livekit-client, loaded on the first connect so the entry screen does
// not pay for it. Opus (LiveKit's audio codec, 48 kHz) carries the ONE track mic.ts opened with echo cancellation
// and noise suppression on; nothing here calls getUserMedia (iOS Safari: never open the microphone twice).
//
// The track belongs to mic.ts (the mic ring reads it too), so LiveKit must never stop it: tracks are not stopped on
// unpublish (stopLocalTrackOnUnpublish false, unpublishTrack(track, false)) nor on our own disconnect
// (disconnect(false)); a connection lost with the network then leaves the track alive to publish again on resume.
// The page going away is App.tsx's pagehide handler (Publisher.stop), not LiveKit's own page-leave hook.
// MAJLIS-62 (48b): the client subscribes to every other seat automatically (autoSubscribe on) and the SERVER decides
// whether it may: the seat's grant and every UpdateParticipant carry canSubscribe from OUR record (its Listen, in a
// live-audio room). Revoking it stops the audio at the audio server and restoring it resubscribes by itself (measured,
// MAJLIS-48), so nothing here subscribes or unsubscribes by hand. A subscribed remote audio track is handed to the
// hearing module as its raw MediaStreamTrack, which plays it through a plain <audio> element: never track.attach(),
// and webAudioMix is OFF, so no remote voice ever goes through Web Audio (the echo canceller cannot see that path).
// The token is passed to LiveKit only; LiveKit redacts it from its own signal-URL log line, and its log level is
// held at "warn".
//
// MAJLIS-61 (the safety floor): the server revokes canPublish while the seat is muted, and LiveKit unpublishes the
// track server-side. Two local-participant events are reported to the publisher:
// - LocalTrackUnpublished, only for OUR current track (not for our own unpublish() of an older one);
// - ParticipantPermissionsChanged, WITHOUT its argument: livekit-client passes the PREVIOUS permission (measured,
//   MAJLIS-48), so canPublish() reads localParticipant.permissions, the current one, every time it is asked.
import type { MediaRoom, MediaRoomFactory } from "./publish";
import { isVoiceIdentity } from "./voiceTrack";

type LiveKit = typeof import("livekit-client");

let loaded: Promise<LiveKit> | null = null;
const load = (): Promise<LiveKit> =>
  (loaded ??= import("livekit-client").then((lk) => {
    lk.setLogLevel("warn");
    return lk;
  }));

/** MAJLIS-71 (the `?micdiag=1` panel, micdiag.tsx): told of the room's connection-state changes and of the local
 * participant's track events, by NAME only. Given no observer (the default, and always without the flag), the adapter
 * attaches not one extra listener. */
export interface RoomObserver {
  connectionState(state: string): void;
  localTrack(event: "published" | "unpublished" | "muted" | "unmuted"): void;
  /** MAJLIS-116 (T-141): RoomEvent.Disconnected's reason (DISCONNECT_REASONS below; "other" for a code outside it;
   * null when livekit-client gave none), beside the page's visibility state at that moment. */
  disconnected(reason: string | null, visibility: string): void;
}

/** livekit-client's DisconnectReason: RoomEvent.Disconnected's argument, a numeric protobuf enum re-exported from
 * @livekit/protocol. SOURCE (checked 2026-09-25 against livekit-client 2.22.3's pinned @livekit/protocol): 0
 * UNKNOWN_REASON, 1 CLIENT_INITIATED, 2 DUPLICATE_IDENTITY, 3 SERVER_SHUTDOWN, 4 PARTICIPANT_REMOVED, 5 ROOM_DELETED,
 * 6 STATE_MISMATCH, 7 JOIN_FAILURE, 8 MIGRATION, 9 SIGNAL_CLOSE, 10 ROOM_CLOSED, 11 USER_UNAVAILABLE,
 * 12 USER_REJECTED, 13 SIP_TRUNK_FAILURE. MAJLIS-116 (T-141): named HERE, by a fixed table this codebase owns, never
 * by reading the library's own enum object back; a code outside this table is "other", never guessed. */
const DISCONNECT_REASONS: Readonly<Record<number, string>> = {
  0: "unknownReason",
  1: "clientInitiated",
  2: "duplicateIdentity",
  3: "serverShutdown",
  4: "participantRemoved",
  5: "roomDeleted",
  6: "stateMismatch",
  7: "joinFailure",
  8: "migration",
  9: "signalClose",
  10: "roomClosed",
  11: "userUnavailable",
  12: "userRejected",
  13: "sipTrunkFailure",
};

export function liveKitRoomFactory(
  loader: () => Promise<LiveKit> = load,
  observer?: RoomObserver,
  // MAJLIS-116 (T-141): the page's visibility at the moment of a Disconnected event. A default that reads the real
  // `document` lazily - never evaluated unless an observer is given AND Disconnected actually fires - so this file
  // needs no injected Document for every other test (livekitRoom.test.ts runs under vitest's "node" environment).
  visibility: () => string = () => document.visibilityState,
): MediaRoomFactory {
  return ({ onDisconnected, onUnpublished, onPermissionsChanged, onRemoteAudio, onRemoteAudioGone, onVoiceTrack }) => {
    let room: InstanceType<LiveKit["Room"]> | null = null;
    // MAJLIS-140 (D-59, P-39): a passive seat's voice track - the `voice-<seat>` participant's one audio publication.
    // Subscribed only while the seat wants it (media/voiceTrack.ts); played like any remote voice (onRemoteAudio).
    let voicePub: { setSubscribed(on: boolean): void } | null = null;
    let track: MediaStreamTrack | null = null;
    let lk: LiveKit | null = null;
    let ours = false; // set while we disconnect on purpose: not a lost connection

    const api: MediaRoom = {
      async connect(url, token) {
        lk = await loader();
        if (ours) throw new Error("disconnected before connecting");
        room = new lk.Room({
          adaptiveStream: false,
          dynacast: false,
          stopLocalTrackOnUnpublish: false,
          disconnectOnPageLeave: false, // App.tsx owns pagehide (disconnect(false) keeps the track for a bfcache return)
          webAudioMix: false, // MAJLIS-62: remote voices through <audio> elements only (the echo canceller's path)
        });
        room.on(lk.RoomEvent.Disconnected, (reason?: unknown) => {
          // MAJLIS-116 (T-141): observation only, before the app logic below - never changes what onDisconnected sees.
          // `unknown` (never trusting the library's declared type): a known numeric code names itself, anything else
          // (an unrecognized number, a future non-numeric value) is "other", absent is null.
          const name = typeof reason === "number" ? (DISCONNECT_REASONS[reason] ?? "other") : reason === undefined ? null : "other";
          observer?.disconnected(name, visibility());
          if (!ours) onDisconnected();
        });
        room.localParticipant.on(lk.ParticipantEvent.LocalTrackUnpublished, (pub) => {
          // our own unpublish() cleared `track` first; anything else that is not our current track is not ours
          if (!track || pub?.track?.mediaStreamTrack !== track) return;
          track = null;
          onUnpublished?.();
        });
        // the argument is the PREVIOUS permission: deliberately not read (canPublish() reads the current one)
        room.localParticipant.on(lk.ParticipantEvent.ParticipantPermissionsChanged, () => onPermissionsChanged?.());
        // MAJLIS-62: another seat's voice, subscribed by the server's say-so; its raw track, never track.attach()
        const audio = lk.Track.Kind.Audio;
        room.on(lk.RoomEvent.TrackSubscribed, (track, publication, participant) => {
          if (track.kind === audio) onRemoteAudio?.(publication.trackSid, track.mediaStreamTrack);
          if (track.kind === audio && isVoiceIdentity(participant.identity)) onVoiceTrack?.("subscribed");
        });
        room.on(lk.RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
          if (track.kind === audio) onRemoteAudioGone?.(publication.trackSid);
          if (track.kind === audio && isVoiceIdentity(participant.identity)) onVoiceTrack?.("unsubscribed");
        });
        // MAJLIS-140: the voice track's own lifecycle (a passive room only: nobody else is ever `voice-...`)
        room.on(lk.RoomEvent.TrackPublished, (publication, participant) => {
          if (publication.kind !== audio || !isVoiceIdentity(participant.identity)) return;
          voicePub = publication;
          onVoiceTrack?.("published");
        });
        room.on(lk.RoomEvent.TrackUnpublished, (publication, participant) => {
          if (!isVoiceIdentity(participant.identity) || voicePub !== publication) return;
          voicePub = null;
          onVoiceTrack?.("gone");
        });
        room.on(lk.RoomEvent.TrackSubscriptionFailed, (_sid, participant) => {
          if (isVoiceIdentity(participant.identity)) onVoiceTrack?.("failed");
        });
        room.on(lk.RoomEvent.ParticipantDisconnected, (participant) => {
          if (!isVoiceIdentity(participant.identity) || !voicePub) return;
          voicePub = null;
          onVoiceTrack?.("gone");
        });
        if (observer) {
          // MAJLIS-71: observation only - each handler passes an event name on and does nothing else
          room.on(lk.RoomEvent.ConnectionStateChanged, (state) => observer.connectionState(String(state)));
          const local = room.localParticipant;
          local.on(lk.ParticipantEvent.LocalTrackPublished, () => observer.localTrack("published"));
          local.on(lk.ParticipantEvent.LocalTrackUnpublished, () => observer.localTrack("unpublished"));
          local.on(lk.ParticipantEvent.TrackMuted, () => observer.localTrack("muted"));
          local.on(lk.ParticipantEvent.TrackUnmuted, () => observer.localTrack("unmuted"));
        }
        await room.connect(url, token, { autoSubscribe: true });
        // MAJLIS-140: a voice track already there when we joined (TrackPublished is only for later ones)
        for (const participant of room.remoteParticipants.values()) {
          if (!isVoiceIdentity(participant.identity)) continue;
          for (const publication of participant.audioTrackPublications.values()) {
            voicePub = publication;
            onVoiceTrack?.("published");
          }
        }
      },
      setVoiceSubscribed(want) {
        voicePub?.setSubscribed(want);
      },
      async publish(t, source) {
        if (!room || !lk) throw new Error("publish before connect");
        if (source !== "microphone") throw new Error(`unsupported source ${String(source)}`);
        track = t;
        try {
          await room.localParticipant.publishTrack(t, { source: lk.Track.Source.Microphone });
        } catch (e) {
          if (track === t) track = null; // not published: nothing of ours for a later unpublish() to take down
          throw e;
        }
      },
      canPublish() {
        return room?.localParticipant.permissions?.canPublish === true;
      },
      async mute() {
        await micPublication()?.mute();
      },
      async unmute() {
        await micPublication()?.unmute();
      },
      async unpublish() {
        const t = track;
        track = null;
        if (room && t) await room.localParticipant.unpublishTrack(t, false);
      },
      async disconnect() {
        ours = true;
        await room?.disconnect(false);
      },
    };

    function micPublication() {
      if (!room || !lk) return undefined;
      return room.localParticipant.getTrackPublication(lk.Track.Source.Microphone);
    }

    return api;
  };
}
